/**
 * ErrorBoundary — Catches unhandled React errors, reports to PostHog,
 * and renders a user-friendly fallback UI.
 */

import React, { Component, ErrorInfo, ReactNode } from "react";
import posthog from "posthog-js";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Report to PostHog
    posthog.capture("$exception", {
      $exception_message: error.message,
      $exception_type: error.name,
      $exception_stack_trace_raw: error.stack,
      componentStack: errorInfo.componentStack,
    });

    console.error("ErrorBoundary caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-8">
          <div className="max-w-md w-full bg-white rounded-xl shadow-md overflow-hidden">
            <div className="bg-red-50 border-l-4 border-red-500 p-6">
              <h2 className="text-red-700 font-semibold text-lg mb-2">
                Something went wrong
              </h2>
              <p className="text-red-600 text-sm mb-4">
                An unexpected error occurred. This has been automatically
                reported.
              </p>
              {this.state.error && (
                <p className="text-red-400 text-xs font-mono bg-red-100 rounded p-2 mb-4">
                  {this.state.error.message}
                </p>
              )}
              <button
                onClick={() => window.location.reload()}
                className="bg-red-600 text-white text-sm px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
